import { useState, useEffect, useMemo, useCallback } from "react";
import "../../../App.css";
import PageHeader from "../../Page/page_header";
import PageContextProvider from "../../Page/page_context";
import { useSidebar } from "../../ui/sidebar";
import PageContainer from "../../Page/page_container";
import { NonogramProvider } from "./Generator From Image/nonogram_context";
import { toast } from "sonner";
import { get, put } from "../../../lib/api";
import { PencilIcon } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const NonogramPage = ({ title, containers, cols, type }) => {
  const { game_id, mode } = useSidebar();
  const [nonograms, setNonograms] = useState([]);
  const [allNonograms, setAllNonograms] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedNonogram, setSelectedNonogram] = useState("");

  // Fetch challenges once
  const fetchChallenges = useCallback(async () => {
    try {
      const response = await get(
          `/challenges?method=getAllChallenges&game_id=${game_id}`
      );
      const transformed = response.game_ID.map((_, i) => ({
        game_ID: response.game_ID[i],
        date: response.date[i],
        daily_challenge_info: response.daily_challenge_info[i],
      }));
      setNonograms(
          transformed.sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
      );
    } catch (err) {
      console.error("Error fetching challenges:", err);
      toast.error("Failed to fetch challenges");
    }
  }, [game_id]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Fetch available nonograms
  useEffect(() => {
    const fetchNonograms = async () => {
      try {
        const response = await get(
            `/statistics?method=getDailyChallengeNonogram&gameid=${game_id}`
        );
        setAllNonograms(response || []);
      } catch (err) {
        console.error("Error fetching nonograms:", err);
      }
    };
    fetchNonograms();
  }, [game_id]);

  // Compute timeline once per nonograms update
  const timeline = useMemo(() => {
    const today = new Date();
    const byDate = (offset) => {
      const d = new Date(today);
      d.setDate(d.getDate() + offset);
      return nonograms.find(
          (n) => new Date(n.date).toDateString() === d.toDateString()
      );
    };
    return {
      yesterday: byDate(-1),
      today: byDate(0),
      tomorrow: byDate(1),
      "Day After Tomorrow": byDate(2),
    };
  }, [nonograms]);

  // Split containers into columns (memoized)
  const containerCols = useMemo(() => {
    const perCol = Math.ceil((containers?.length || 0) / cols);
    return Array.from({ length: cols }, (_, i) =>
        (containers || []).slice(i * perCol, (i + 1) * perCol)
    );
  }, [containers, cols]);

  // Dark mode toggle
  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  // Handle editing challenge
  const handleEditChallenge = (challenge) => {
    setSelectedChallenge(challenge);
    setSelectedNonogram(challenge.daily_challenge_info);
    setIsEditModalOpen(true);
  };

  const handleUpdateNonogram = async () => {
    if (!selectedChallenge || !selectedNonogram) {
      toast.error("Please select a nonogram");
      return;
    }
    try {
      const body = {
        daily_challenge_info: selectedNonogram,
        method: "updateDailyNonogram",
        date: selectedChallenge.date,
      };
      await put(body, "/challenges");
      await fetchChallenges();
      toast.success("Challenge updated successfully");
      setIsEditModalOpen(false);
      setSelectedChallenge(null);
      setSelectedNonogram("");
    } catch (err) {
      console.error("Failed to update challenge:", err);
      toast.error("Failed to update challenge");
    }
  };

  return (
      <NonogramProvider>
        <PageContextProvider>
          <div className="min-h-screen default_page_style">
            <div className="!w-screen sm:!w-max mx-auto px-4">
              <PageHeader title={title} />

              {/* Timeline */}
              <div className="mt-4 dark:!bg-[var(--header-color-dark)] bg-white text-black dark:!text-white rounded-lg shadow-md p-4 overflow-x-auto">
                <div className="flex justify-between items-center min-w-[640px]">
                  <div className="flex space-x-4 md:space-x-12 w-full">
                    {Object.entries(timeline).map(([day, nonogram]) => (
                        <div
                            key={day}
                            className={`flex-1 flex flex-col items-center p-2 md:p-4 rounded-lg ${
                                day === "today" ? "bg-primary/5" : ""
                            }`}
                        >
                          <div className="text-xs md:text-sm font-medium text-gray-500 mb-2">
                            {day}
                          </div>
                          <div className="flex items-center space-x-2">
                            <div
                                className={`w-2 h-2 rounded-full ${
                                    day === "yesterday"
                                        ? "bg-green-500"
                                        : day === "today"
                                            ? "bg-amber-500"
                                            : "bg-blue-500"
                                }`}
                            />
                            <span className="text-xs md:text-base font-semibold truncate max-w-[120px] md:max-w-full">
                          {nonogram?.daily_challenge_info || "No Challenge"}
                        </span>
                          </div>
                          <div className="text-[10px] md:text-xs text-gray-400 mt-1">
                            {nonogram?.date &&
                                new Date(nonogram.date).toLocaleDateString()}
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Containers + Challenge list */}
              <div className="mt-4 grid gap-6 md:grid-cols-1 xl:grid-cols-3 xl:[&>*:first-child]:col-span-2">
                {/* Left: Containers */}
                <div className="space-y-4">
                  {containerCols.map((col, colIndex) => (
                      <div key={colIndex} className="space-y-4">
                        {col.map((container, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-lg shadow-md"
                            >
                              <PageContainer title={container.title} game_id={game_id} />
                            </div>
                        ))}
                      </div>
                  ))}
                </div>

                {/* Right: Challenge list */}
                <div className="bg-[var(--header-color-light)] dark:bg-[var(--header-color-dark)] rounded-lg shadow-md p-4 dark:text-white">
                  <h3 className="text-lg font-semibold mb-4">Challenge List</h3>
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                    {nonograms.map((challenge) => (
                        <div
                            key={challenge.game_ID}
                            className="flex items-center justify-between p-2 rounded-md hover:bg-purple-700/55"
                        >
                          <div className="flex items-center space-x-3">
                            <div
                                className={`w-2 h-2 rounded-full ${
                                    new Date(challenge.date) < new Date()
                                        ? "bg-green-500"
                                        : new Date(challenge.date).toDateString() ===
                                        new Date().toDateString()
                                            ? "bg-amber-500"
                                            : "bg-blue-500"
                                }`}
                            />
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditChallenge(challenge)}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <span className="text-sm font-medium">
                          {challenge.daily_challenge_info}
                        </span>
                          </div>
                          <span className="text-xs text-gray-500">
                        {new Date(challenge.date).toLocaleDateString()}
                      </span>
                        </div>
                    ))}
                  </div>
                </div>

                {/* Edit Modal */}
                <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                  <DialogContent className="sm:max-w-[425px] dark:bg-[var(--header-color-dark)]">
                    <DialogHeader>
                      <DialogTitle>Edit Challenge</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          Editing challenge for {selectedChallenge?.date}
                        </h4>
                        <Select
                            value={selectedNonogram}
                            onValueChange={setSelectedNonogram}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a nonogram" />
                          </SelectTrigger>
                          <SelectContent>
                            {allNonograms
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((n) => (
                                    <SelectItem key={n.name} value={n.name}>
                                      {n.name}
                                    </SelectItem>
                                ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setIsEditModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateNonogram}
                            disabled={!selectedNonogram}
                            className="bg-purple-700 hover:bg-purple-800"
                        >
                          Update
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </PageContextProvider>
      </NonogramProvider>
  );
};

export default NonogramPage;
